import {useCallback, useEffect, useRef, useState} from "react";
import axiosInstance from "../../config/axios-config";
import {reportingError} from "./analytics-filters";

export default function useDistributorAnalytics(tenantId, language, filters) {
    const [state, setState] = useState({status: "loading", data: null, error: null});
    const scope = useRef(null);
    const {from, to, limit, rankBy = "UNITS"} = filters;
    const key = `${tenantId}:${language}:${from}:${to}:${limit}:${rankBy}`;
    const invalid = reportingError(filters);
    const load = useCallback(async () => {
        scope.current?.controller.abort();
        const current = {controller: new AbortController()};
        scope.current = current;
        const isCurrent = () => scope.current === current && !current.controller.signal.aborted;
        if (invalid) { setState({key, status: "unavailable", data: null, error: invalid}); return; }
        setState({key, status: "loading", data: null, error: null});
        try {
            const {data} = await axiosInstance.get("/api/v1/statics/best-selling-products", {
                params: {startDate: `${from}T00:00:00`, endDate: `${to}T23:59:59.999999999`, limit, rankBy},
                headers: {"Language-Context": (language || "en").toUpperCase()}, signal: current.controller.signal,
                timeout: 15000,
            });
            if (!Array.isArray(data) || data.some(row => !row || typeof row !== "object")) throw new Error("Invalid analytics response");
            if (isCurrent()) setState({key, status: "success", data, error: null});
        } catch (failure) {
            if (isCurrent()) setState({key, status: "error", data: null,
                error: failure.response?.data?.message || "DistributorAnalyticsFailed"});
        }
    }, [from, to, limit, rankBy, language, invalid, key]);

    useEffect(() => {
        load();
        // A previous tenant, filter or closed tab must never supply current chart values.
        return () => { scope.current?.controller.abort(); };
    }, [tenantId, load]);

    return {...(state.key === key ? state : {status: "loading", data: null, error: null}), reload: load};
}
